import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPlayCircle } from 'react-icons/fa';
import './graduados.css';

const graduados = [
  { image: 'https://ensenarteonline.com.co/images/graduados/graduados072026-0.jpeg' },
  { image: 'https://ensenarteonline.com.co/images/graduados/graduados072026-1.jpeg' },
  { image: 'https://ensenarteonline.com.co/images/graduados/graduados072026-2.jpeg' },
  { image: 'https://ensenarteonline.com.co/images/graduados/graduados072026-3.jpeg' },
  { image: 'https://ensenarteonline.com.co/images/graduados/graduados072026-4.jpeg' },
  { image: 'https://ensenarteonline.com.co/images/graduados/graduados072026-5.jpeg' },
  { video: 'https://ensenarteonline.com.co/images/graduados/graduados072026-6.mp4' },
  { video: 'https://ensenarteonline.com.co/images/graduados/graduados072026-7.mp4' },
];

const periodos = ['Julio 2026', 'Septiembre 2026', 'Diciembre 2026', 'Marzo 2027'];
const PAGE_SIZE = 8;

function Graduados() {
  const [paginaActual, setPaginaActual] = useState(0);
  const [showConfetti, setShowConfetti] = useState(true);
  const [playingVideoIndex, setPlayingVideoIndex] = useState(null);
  const sectionRef = useRef(null);
  const videoRefs = useRef({});

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowConfetti(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handlePlayVideo = (index) => {
    setPlayingVideoIndex(index);
  };

  useEffect(() => {
    if (playingVideoIndex === null) return;

    const video = videoRefs.current[playingVideoIndex];
    const wrapper = video?.parentElement?.closest('.video-preview-wrapper');

    if (video) {
      const syncFullscreenState = () => {
        const isFullscreen = !!document.fullscreenElement ||
          !!document.webkitFullscreenElement ||
          !!document.mozFullScreenElement ||
          !!document.msFullscreenElement;

        if (wrapper) {
          wrapper.classList.toggle('fullscreen-video-shell', isFullscreen);
        }
      };

      try {
        video.muted = false;
        video.volume = 1;
        const playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(() => {});
      } catch (error) {
        // ignore browser play restrictions
      }

      video.addEventListener('fullscreenchange', syncFullscreenState);
      video.addEventListener('webkitfullscreenchange', syncFullscreenState);
      video.addEventListener('mozfullscreenchange', syncFullscreenState);
      video.addEventListener('MSFullscreenChange', syncFullscreenState);
      syncFullscreenState();

      return () => {
        video.removeEventListener('fullscreenchange', syncFullscreenState);
        video.removeEventListener('webkitfullscreenchange', syncFullscreenState);
        video.removeEventListener('mozfullscreenchange', syncFullscreenState);
        video.removeEventListener('MSFullscreenChange', syncFullscreenState);
      };
    }
  }, [playingVideoIndex]);

  const grupos = useMemo(() => {
    const gruposPorPagina = [];

    for (let i = 0; i < graduados.length; i += PAGE_SIZE) {
      const titulo = periodos[Math.floor(i / PAGE_SIZE)] || `Grados ${i / PAGE_SIZE + 1}`;
      gruposPorPagina.push({
        title: `Grados ${titulo}`,
        items: graduados.slice(i, i + PAGE_SIZE)
      });
    }

    return gruposPorPagina;
  }, []);

  const grupoActual = grupos[paginaActual];
  const confettiPieces = Array.from({ length: 28 }, (_, index) => ({
    id: index,
    left: `${(index * 13) % 100}%`,
    delay: `${(index % 7) * 0.12}s`,
    duration: `${1.8 + (index % 6) * 0.25}s`,
    color: ['#facc15', '#34d399', '#60a5fa', '#f472b6', '#fb7185', '#a78bfa'][index % 6],
    rotate: `${(index % 10) * 36}deg`
  }));

  return (
    <section ref={sectionRef} className="graduados-section section-animated">
      {showConfetti && (
        <div className="confetti-layer" aria-hidden="true">
          {confettiPieces.map((piece) => (
            <span
              key={piece.id}
              className="confetti-piece"
              style={{
                left: piece.left,
                animationDelay: piece.delay,
                animationDuration: piece.duration,
                background: piece.color,
                transform: `rotate(${piece.rotate})`
              }}
            />
          ))}
        </div>
      )}

      <div className="graduados-container">
        <div className="graduados-header">
          <p className="graduados-kicker">Nuestra comunidad</p>
          <h2>Graduados que inspiran</h2>
          <p className="graduados-description">
            Historias de estudiantes y egresados que encontraron en EnseñArte una ruta para crecer,
            profesionalizarse y cumplir metas con confianza.
          </p>
        </div>

        {grupoActual && (
          <>
            <div className="graduados-batch-header">{grupoActual.title}</div>

            <div className="graduados-grid">
              {grupoActual.items.map((item, index) => {
                const itemKey = `${grupoActual.title}-${index}`;

                return (
                  <article key={itemKey} className="graduado-card">
                    <div
                      className={`graduado-image-wrap${item.video ? ' video-wrap' : ''}`}
                    >
                      {item.video ? (
                        <div className="video-preview-wrapper">
                          {playingVideoIndex === itemKey ? (
                            <video
                              ref={(el) => (videoRefs.current[itemKey] = el)}
                              src={item.video}
                              controls
                              autoPlay
                              playsInline
                              preload="metadata"
                              className="graduado-video"
                            />
                          ) : (
                            <div className="video-preview" onClick={() => handlePlayVideo(itemKey)}>
                              <video
                                ref={(el) => (videoRefs.current[itemKey] = el)}
                                src={item.video}
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                className="video-preview-element"
                              />
                              <div className="video-play-button">
                                <FaPlayCircle size={72} />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <img src={item.image} alt={item.title} />
                          <div className="graduado-overlay" aria-hidden="true" />
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}

        {grupos.length > 1 && (
          <div className="graduados-pagination">
            <button
              className="graduados-page-btn"
              onClick={() => {
                setPlayingVideoIndex(null);
                setPaginaActual((prev) => Math.max(prev - 1, 0));
              }}
              disabled={paginaActual === 0}
            >
              Anterior
            </button>

            <div className="graduados-pager-dots">
              {grupos.map((_, index) => (
                <button
                  key={index}
                  className={`graduados-dot ${index === paginaActual ? 'active' : ''}`}
                  onClick={() => setPaginaActual(index)}
                  aria-label={`Ir a la página ${index + 1}`}
                />
              ))}
            </div>

            <button
              className="graduados-page-btn"
              onClick={() => {
                setPlayingVideoIndex(null);
                setPaginaActual((prev) => Math.min(prev + 1, grupos.length - 1));
              }}
              disabled={paginaActual === grupos.length - 1}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default Graduados;